import * as React from "react";
import { connect } from "react-redux";

import { Alert, Button, Card, Checkbox, Modal, Select, Stack, Tabs, Text, TextInput, Tooltip } from "@mantine/core";
import _ from "lodash";
import { DateTime } from "luxon";
import { bindActionCreators } from "redux";
import {
  createPerson,
  getFullPersonById,
  savePerson,
} from "../../../data/actions/personActions";
import AuthApi from "../../../data/api/authApi";
import { IAssociation } from "../../../types/association/Association";
import { BasketballGrade } from "../../../types/common/basketballGrades";
import { StatisticianGrade } from "../../../types/common/statisticianGrade";
import { Role } from "../../../types/persons/Role";
import {
  IPerson,
  IPersonAssociation,
  IPersonUpdate,
} from "../../../types/persons/person";
import { Privilege } from "../../../types/persons/privilege";
import { CenteredLoader } from "../../common/loader";
import PersonAvatar from "../PersonAvatar";
import BankDetailsTab from "./BankDetailsTab";
import PersonAssociationModal from "./PersonAssociationModal";

import PersonApi from "../../../data/api/personsApi";
import { IFuzzyTally } from "../../../types/imports/ImportPerson";
import { toDateTime, toDateTimeNullable } from "../../../util/date+helper";
import { isNullOrWhitespace } from "../../../util/string";
import FuzzyPersonMatch from "./FuzzyPersonmatch";

const classnames = (props) =>
  Object.keys(props)
    .filter((k) => props[k])
    .join(" ");

interface PersonsEditorProps {
  personId: string | null;
  onSave: Function;
  onCancel: Function;
  selfPrivilege: Privilege;

  // provided
  actions: {
    savePerson: Function;
    createPerson: Function;
    getFullPersonById: (personId: string) => Promise<{ person: IPerson }>;
  };
  // persons: { [id: string]: IPerson }
  associations: { [id: string]: IAssociation };
  associationIdContext?: string;
  self: IPerson;
}
interface PersonsEditorState {
  person?: IPerson;
  personChanges: Partial<IPerson>;
  personId: string | null;
  tab?: string;
  hasChanges: boolean;

  saving?: boolean;
  saveError?: null | string;

  fuzzyTally?: IFuzzyTally<IPerson> | null;
}

export interface PersonsTabProps {
  editMode: "edit" | "new";
  person: IPersonUpdate;
  privilege: Privilege;
  role: Role;

  selfPrivilege: Privilege;

  onEventChange: React.ChangeEventHandler<any>;
  onChange: (key: string, value: any) => void;

  active: boolean;
  refreshPerson: Function;

  associations: { [key: string]: IAssociation };
  associationIdContext?: string;
}

const PersonalTab: React.FunctionComponent<PersonsTabProps> = ({
  person,
  onEventChange,
  active,
  editMode,
}) => (
  <Stack gap="md">
    <Stack gap="xs">
      <Text size="sm" fw={500}>Identifier</Text>
      <TextInput
        disabled
        name="identifier"
        value={person.externalIdentifier}
        onChange={onEventChange}
      />
      <Text size="xs" c="dimmed">
        This is the identifier you can use to reference{" "}
        {person.preferredName || person.firstName}.{" "}
        {editMode == "edit"
          ? "This value was created on import and unique within StadiumSupervisor."
          : "This value is unique and will be created on save."}
      </Text>
    </Stack>
    <Stack gap="xs">
      <Text size="sm" fw={500}>Preferred name</Text>
      <TextInput
        disabled={!active}
        name="preferredName"
        value={person.preferredName}
        placeholder={person.firstName}
        onChange={onEventChange}
      />
    </Stack>
    <Stack gap="xs">
      <Text size="sm" fw={500}>First name</Text>
      <TextInput
        disabled={!active}
        name="firstName"
        value={person.firstName}
        onChange={onEventChange}
      />
    </Stack>
    <Stack gap="xs">
      <Text size="sm" fw={500}>Last name</Text>
      <TextInput
        disabled={!active}
        name="lastName"
        value={person.lastName}
        onChange={onEventChange}
      />
    </Stack>
    <hr />
    {/* <Stack gap="sm">
        <Text size="sm" fw={500}>DOB</Text>
        <TextInput disabled={!active} name="dob" value={person.dob ? person.dob : undefined} onChange={onRootChange} />
        TODO: Reintroduce with DatePicker when DOB editing is enabled.
    </Stack> */}
  </Stack>
);

const ContactTab: React.FunctionComponent<PersonsTabProps> = ({
  person,
  onEventChange,
  active,
}) => (
  <Stack gap="md">
    <Stack gap="sm">
      <Text size="sm" fw={500}>Mobile number</Text>
      <TextInput
        disabled={!active}
        name="mobile"
        value={person.mobile}
        onChange={onEventChange}
      />
    </Stack>
    <Stack gap="sm">
      <Text size="sm" fw={500}>Email address</Text>
      {person.emailVerified && (
        <Text size="sm" fw={500} style={{ color: "var(--color-text-disabled)" }}>
          <em>
            This email has been verified. Changing this value will mean the user
            must revalidate their email address next time they log in.
          </em>
        </Text>
      )}
      {!person.email && (
        <Text size="sm" fw={500} style={{ color: "var(--color-text-disabled)" }}>
          <em>
            This person doesn't have an email address, changing this field
            creates a new unverified email.
          </em>
        </Text>
      )}
      <TextInput
        disabled={!active}
        name="email"
        type="email"
        value={person.email}
        onChange={onEventChange}
      />
    </Stack>
  </Stack>
);

const RefereeTab: React.FunctionComponent<PersonsTabProps> = ({
  person,
  onChange,
  onEventChange,
  active,
  role,
}) => {
  if (!Role.hasRole(role, Role.referee)) {
    return (
      <p>
        <em>
          {person.fullName || "This person"} doesn't have the 'Referee' Role,
          please add it through the association section{" "}
        </em>
      </p>
    );
  }
  const referee: {
    id?: string;
    grade?: number;
    started?: Date;
    plus?: boolean;
    isInterestedInPromotion?: boolean;
  } = person.referee || {};

  return (
    <Stack gap="md">
      <Stack gap="sm">
        <Text size="sm" fw={500}>VBRA number</Text>
        <TextInput
          disabled={!active}
          name="referee.id"
          value={referee.id || ""}
          onChange={onEventChange}
        />
      </Stack>
      <Stack gap="sm">
        <Text size="sm" fw={500}>Referee Grade</Text>
        <p style={{ color: "gray" }}>
          Promoting a referee has impliciations for their pay. After you promote
          a referee, you must reallocate their games to ensure they're getting
          the correct pay tally.
        </p>
        <Select
          disabled={!active}
          name="referee.grade"
          value={referee.grade?.toString()}
          onChange={(value) => {
            onChange("referee.grade", Number(value));
          }}
          data={BasketballGrade.all().map((b) => ({
            value: b.toString(),
            label: BasketballGrade.toString(b)
          }))}
        />
      </Stack>
      <Stack gap="sm">
        <Text size="sm" fw={500}>Plus</Text>
        <p style={{ color: "gray" }}>
          A plus referee is a referee who is recognised for their service.
        </p>
        <Checkbox
          style={{
            marginLeft: "0px",
          }}
          disabled={!active}
          checked={referee.plus}
          onChange={(e) => onChange("referee.plus", e.currentTarget.checked)}
        />
        <Text size="sm" fw={500} style={{ marginLeft: "25px", fontSize: "15px" }}>
          Is plus referee
        </Text>
      </Stack>
      <Stack gap="sm">
        <Text size="sm" fw={500}>Is interested in promotion</Text>
        <p style={{ color: "gray" }}>
          A visual flag that demonstrates to allocators that a referee is
          looking to be promoted, and would be interested in more difficult
          games:
        </p>
        <Checkbox
          style={{
            marginLeft: "0px",
          }}
          disabled={!active}
          checked={referee.isInterestedInPromotion || false}
          onChange={(e) => onChange("referee.isInterestedInPromotion", e.currentTarget.checked)}
        />
        <Text size="sm" fw={500} style={{ marginLeft: "25px", fontSize: "15px" }}>
          Interested in promotion
        </Text>
      </Stack>
      <hr />
      <h5>Internal</h5>
      <Stack gap="sm">
        <Text size="sm" fw={500}>Started</Text>
        <TextInput
          disabled
          name="referee.started"
          value={toDateTimeNullable(referee.started)?.toLocaleString?.(
            DateTime.DATE_FULL,
          )}
          onEventChange={onChange}
        />
      </Stack>
    </Stack>
  );
};

const ScoretableTab: React.FunctionComponent<PersonsTabProps> = ({
  person,
  onChange,
  active,
  role,
}) => {
  if (!Role.hasRole(role, Role.scoretable)) {
    return (
      <p>
        <em>
          {person.fullName || "This person"} doesn't have the 'Scoretable' Role,
          please add it through the association section{" "}
        </em>
      </p>
    );
  }
  const scoretable: {
    id?: string | null;
    grade?: number | null;
    started?: Date | null;
  } = person.scoretable || {};

  return (
    <Stack gap="md">
      <Stack gap="sm">
        <Text size="sm" fw={500}>Scoretable Grade</Text>
        <p style={{ color: "gray" }}>
          Promoting a scoretable official has impliciations for their pay. After
          you promote an official, you must reallocate their games to ensure
          they're getting the correct pay.
        </p>
        <Select
          disabled={!active}
          name="scoretable.grade"
          value={(scoretable.grade || 0).toString()}
          onChange={(value) => {
            onChange("scoretable.grade", Number(value));
          }}
          data={_.range(0, 5).map((v) => ({
            value: v.toString(),
            label: v !== 0 ? `Level ${v}` : "Ungraded"
          }))}
        />
      </Stack>
    </Stack>
  );
};

const StatisticianTab: React.FunctionComponent<PersonsTabProps> = ({
  person,
  onChange,
  active,
  role,
}) => {
  if (!Role.hasRole(role, Role.statistician)) {
    return (
      <p>
        <em>
          {person.fullName || "This person"} doesn't have the 'Statistician'
          Role, please add it through the association section{" "}
        </em>
      </p>
    );
  }

  const statistician = person.statistician || { grade: StatisticianGrade.level0, fibaAccredited: false };

  return (
    <Stack gap="md">
      <Stack gap="sm">
        <Text size="sm" fw={500}>Statistician Grade</Text>
        <p style={{ color: "gray" }}>
          Statistician levels determine their base pay on games. Update carefully and ensure
          any existing appointments reflect the new level.
        </p>
        <Select
          disabled={!active}
          name="statistician.grade"
          value={(statistician.grade || 0).toString()}
          onChange={(value) => {
            onChange("statistician.grade", Number(value));
          }}
          data={StatisticianGrade.all().map((grade) => ({
            value: grade.toString(),
            label: grade === StatisticianGrade.level0 ? "Ungraded" : `Level ${grade}`,
          }))}
        />
      </Stack>
      <Stack gap="sm">
        <Text size="sm" fw={500}>FIBA Accredited</Text>
        <Checkbox
          style={{ marginLeft: 0 }}
          disabled={!active}
          checked={!!statistician.fibaAccredited}
          onChange={(e) =>
            onChange("statistician.fibaAccredited", e.currentTarget.checked)
          }
        />
        <Text size="sm" fw={500} style={{ marginLeft: "25px", fontSize: "15px" }}>
          Is FIBA Accredited
        </Text>
      </Stack>
    </Stack>
  );
};

const RefereeCoachTab: React.FunctionComponent<PersonsTabProps> = ({
  person,
  onChange,
  active,
  role,
}) => {
  if (!Role.hasRole(role, Role.refereeCoach)) {
    return (
      <p>
        <em>
          {person.fullName || "This person"} doesn't have the 'RefereeCoach'
          Role, please add it through the association section{" "}
        </em>
      </p>
    );
  }
  const refereeCoach: { grade?: number | null } = person.refereeCoach || {};

  return (
    <Stack gap="md">
      <Stack gap="sm">
        <Text size="sm" fw={500}>Referee Coach Grade</Text>
        <p style={{ color: "gray" }}>
          Promoting a refereeCoach official has impliciations for their pay.
          After you promote an official, you must reallocate their games to
          ensure they're getting the correct pay.
        </p>
        {(refereeCoach.grade || 0) === 0 && (
          <p style={{ color: "gray" }}>
            An ungraded referee coach will get the same base pay as their
            referee grade.
          </p>
        )}
        <Select
          disabled={!active}
          name="refereeCoach.grade"
          value={(refereeCoach.grade || 0).toString()}
          onChange={(value) => {
            onChange("refereeCoach.grade", Number(value));
          }}
          data={_.range(0, 5).map((v) => ({
            value: v.toString(),
            label: v !== 0 ? `Grade ${v}` : "Ungraded"
          }))}
        />
      </Stack>
    </Stack>
  );
};

const AssociationsTab: React.FunctionComponent<PersonsTabProps> = ({
  person,
  associations,
  selfPrivilege,
  refreshPerson,
  onChange,
  editMode,
}) => {
  const [isEditing, toggleEdit] = React.useState(false);

  return (
    <>
      <p>
        <b>Active Associations</b>
      </p>
      {Privilege.canManage(selfPrivilege) && (
        <Button
          onClick={() => toggleEdit(!isEditing)}
          style={{ float: "right" }}
        >
          Add
        </Button>
      )}
      {person.associations
        .filter((a) => !a.finish)
        .map((a) => (
          <Card key={a.associationId} padding="sm">
            <AssociationTabRow association={a} associations={associations} />
          </Card>
        ))}
      {isEditing && (
        <PersonAssociationModal
          returnUnsaved={editMode == "new"}
          personId={person._id}
          associations={Object.values(associations)}
          onSave={(assosc) => {
            if (editMode == "new") {
              const prev = person.associations || [];
              onChange("associations", [...prev, assosc]);
            } else {
              refreshPerson();
            }
            toggleEdit(false);
          }}
          onCancel={() => toggleEdit(false)}
        />
      )}
    </>
  );
};

const AssociationTabRow: React.FunctionComponent<{
  association: IPersonAssociation;
  associations: { [key: string]: IAssociation };
}> = ({ association, associations }) => {
  const assocName =
    association.associationId && association.associationId in associations
      ? associations[association.associationId].name
      : association.associationId;
  return (
    <div>
      <p style={{ color: "gray" }}>{assocName.toUpperCase()}</p>
      <table>
        <tbody>
          <tr>
            <td>Start</td>
            <td>{toDateTime(association.start).toLocaleString(DateTime.DATETIME_FULL)}</td>
          </tr>
          {association.finish && (
            <tr>
              <td>Finish</td>
              <td>{toDateTime(association.finish).toLocaleString(DateTime.DATETIME_FULL)}</td>
            </tr>
          )}
          <tr>
            <td>Role</td>
            <td>{Role.getRoleNames(association.role).join(", ")}</td>
          </tr>
          <tr>
            <td style={{ paddingRight: "10px" }}>Privilege</td>
            <td>{Privilege.getNames(association.privilege).join(", ")}</td>
          </tr>
          <tr>
            <td>Tenure</td>
            <td>{association.tenure ? association.tenure.charAt(0).toUpperCase() + association.tenure.slice(1) : 'Permanent'}</td>
          </tr>
          {association.adminPayrate && <tr>
            <td>Admin Payrate</td>
            <td>{association.adminPayrate.toFixed(2)}</td>
          </tr>}
        </tbody>
      </table>
    </div>
  );
};

const AdvancedTab: React.FunctionComponent<PersonsTabProps> = ({
  person,
  editMode,
}) => (
  <Alert color="red">
    <p>Warning: Actions in this section may cause damage</p>
    <Button
      color="red"
      variant="outline"
      disabled={editMode !== "edit"}
      onClick={() => {
        AuthApi.triggerPasswordReset(person.externalIdentifier)
          .then((resp) => alert(resp))
          .catch((er) => alert(er.message));
      }}
    >
      Reset password
    </Button>
  </Alert>
);

class PersonsEditor extends React.PureComponent<
  PersonsEditorProps,
  PersonsEditorState
> {
  _mounted = false;

  constructor(props, content) {
    super(props, content);

    this.trySetNewPerson(this.props.personId, this.props);

    this.save = this.save.bind(this);
    this.toggle = this.toggle.bind(this);
    this.refresh = this.refresh.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onEventChange = this.onEventChange.bind(this);
    this.associateExistingPerson = this.associateExistingPerson.bind(this);
    this.definitelyCreatePersonFromFuzzyMatch =
      this.definitelyCreatePersonFromFuzzyMatch.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillReceiveProps(nextProps: Readonly<PersonsEditorProps>) {
    // return if it's the same person
    this.trySetNewPerson(nextProps.personId, nextProps);
  }

  trySetNewPerson(personId, props) {
    // stop if we have state and they're the same person
    if (!!this.state && personId === this.state.personId) return;

    const newState = {
      personId,
      // person: _.cloneDeep(props.persons[personId]),
      personChanges: { _id: personId },
      hasChanges: false,
    };

    if (this.state) {
      this.setState(newState);
    } else {
      this.state = {
        ...newState,
        tab: "Personal",
      };
    }
    this.refresh(personId);
  }

  refresh(personId) {
    if (personId && personId.length > 0) {
      this.setState({ saving: true });
      return this.props.actions
        .getFullPersonById(personId)
        .then(({ person }) => {
          this.setState((state) => ({ ...state, saving: false, person }));
          return person;
        })
        .catch(alert);
    }
    const person = {
      _id: "",
      dob: "",
      mobile: "",
      firstName: "",
      lastName: "",
      fullName: "",
      image: "",
      email: "",
      associations: [],
      externalIdentifier: "",
    };
    if (this._mounted) this.setState({ person });
    else {
      this.state = { ...this.state, person };
    }
  }

  onEventChange(e) {
    const t = e.currentTarget;
    if (!t.name) {
      throw new Error(
        "An internal error has occurred, there is no name property on the currently edited field",
      );
    }
    const { value } = t;
    this.onChange(t.name, value);
  }

  onChange(key: string, value: any) {
    if (!key) {
      throw new Error(
        "An internal error has occurred, there is no name property on the currently edited field",
      );
    }
    const parts: string[] = key.split(".");

    if (!this.state.person) {
      return console.log(
        `Discarding change to '${key}' until person is loaded`,
      );
    }

    this.setState((state) => {
      if (!state.person) return { ...state };
      return {
        ...state,
        hasChanges: true,
        person: this.getNestedChange(state.person, parts, value) as IPerson,
        personChanges: this.getNestedChange(
          state.personChanges,
          parts,
          value,
        ) as IPerson,
      };
    });
  }

  getNestedChange(obj: object, keyParts: string[], newValue: any): object {
    if (keyParts.length === 0) {
      throw Error(
        "An internal error has occurred: Invalid number of key parts",
      );
    }
    const key = keyParts[0];
    if (keyParts.length === 1) return { ...obj, [key]: newValue };

    const nested = obj[key] || {}; // create parts of the object we don't have
    return {
      ...obj,
      [key]: this.getNestedChange(nested, keyParts.slice(1), newValue),
    };
  }

  save() {
    this.setState((state) => ({ ...state, saving: true, saveError: null }));
    const { person } = this.state;
    if (!person) {
      return alert(
        "An internal error has occurred: no person was found to save",
      );
    }
    if (!person._id) {
      PersonApi.fuzzyTally(person)
        .then((resp) => {
          if (resp) {
            this.setState({ fuzzyTally: resp });
          } else {
            this.props.actions
              .createPerson(this.state.person)
              .then((person) =>
                this.setState({ person, saving: false, hasChanges: false }),
              )
              .catch((er) =>
                this.setState({ saving: false, saveError: er.message }),
              );
          }
        })
        .catch((er) => this.setState({ saving: false, saveError: er.message }));

      // there IS a person._id
      // this.props.actions.createPerson(this.state.person)
      //     .then(person => this.setState({person, saving: false, hasChanges: false}))
      //     .catch(er => this.setState({saving: false, saveError: er.message}))
    } else {
      this.props.actions
        .savePerson(this.state.personChanges)
        .then(() => {
          this.setState((state) => ({
            ...state,
            saving: false,
            hasChanges: false,
          }));
          this.trySetNewPerson(this.state.personId, this.props);
        })
        .catch((er) => {
          this.setState((state) => ({
            ...state,
            saving: false,
            saveError: er.message,
          }));
        });
    }
  }

  toggle(tabKey) {
    this.setState((state) => ({ ...state, tab: tabKey }));
  }

  getTabs() {
    const tabs = {
      Personal: PersonalTab,
      Contact: ContactTab,
    };

    // const roles = this.state.person.associations.map(a => a.role).reduce((prev, cur) => prev | cur)
    // if (Role.hasRole(roles, Role.referee)) {
    // }

    tabs.Referee = RefereeTab;
    tabs.Scoretable = ScoretableTab;
    tabs.Statistician = StatisticianTab;
    tabs["Referee coach"] = RefereeCoachTab;
    tabs["Bank Details"] = BankDetailsTab;
    tabs.Associations = AssociationsTab;
    tabs.Advanced = AdvancedTab;

    return tabs;
  }

  personValidationErrors(person: IPersonUpdate) {
    const invalidFields: string[] = [];

    if (isNullOrWhitespace(person.firstName)) invalidFields.push("First name");
    if (isNullOrWhitespace(person.lastName)) invalidFields.push("Last name");
    // if (isNullOrWhitespace(person.email)) invalidFields.push("Email")
    // if (isNullOrWhitespace(person.mobile)) invalidFields.push("Mobile")
    if (person.associations.length <= 0) invalidFields.push("Associations");

    return invalidFields;
  }

  async associateExistingPerson(existingPerson: IPerson) {
    const { person } = this.state;
    if (!person)
      return alert("An internal error occurred when associating user");

    person.associations.forEach(async (assoc) => {
      await PersonApi.associatePerson(existingPerson._id, assoc);
    });

    this.refresh(existingPerson._id);

    this.setState({ fuzzyTally: null });
  }

  definitelyCreatePersonFromFuzzyMatch() {
    this.props.actions
      .createPerson(this.state.person)
      .then((person) =>
        this.setState({
          person,
          saving: false,
          personChanges: { _id: person._id },
          hasChanges: false,
          fuzzyTally: null,
        }),
      )
      .catch((er) => this.setState({ saving: false, saveError: er.message }));
  }

  render() {
    const { person } = this.state;

    if (!person) return <CenteredLoader />;

    // if there's a person._id, we're editing
    const editMode = person._id ? "edit" : "new";

    const tabs = this.getTabs();

    const roles = person.associations
      .map((a) => a.role)
      .reduce((prev, cur) => prev | cur, 0);
    const privilege = person.associations
      .map((a) => a.privilege)
      .reduce((prev, cur) => prev | cur, 0);

    const invalidFields = this.personValidationErrors(person);
    const saveButtonVisible = editMode == "new" || this.state.hasChanges;

    return (
      <div style={{ paddingBottom: "10px" }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <PersonAvatar image={person.image} size="small" />
          <div>
            <Text size="lg" fw={500} style={{ margin: 0 }}>
              {person.firstName} {person.lastName}
            </Text>
            {person.referee && person.referee.grade && (
              <Text size="sm" c="dimmed" style={{ margin: 0 }}>
                {BasketballGrade.toString(person.referee.grade)}
              </Text>
            )}
          </div>
        </div>
        <p style={{ margin: "10px" }}>{"" /* sharedAssociationText */}</p>

        <Tabs value={this.state.tab} onChange={this.toggle}>
          <Tabs.List>
            {Object.keys(tabs).map((k) => (
              <Tabs.Tab key={k} value={k}>
                {k}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {Object.keys(tabs).map((k) => (
            <Tabs.Panel key={k} value={k} pt="md">
              {React.createElement(tabs[k], {
                person,
                editMode,
                onChange: this.onChange,
                onEventChange: this.onEventChange,
                refreshPerson: () => this.refresh(person._id),
                active: !this.state.saving,
                role: roles,
                privilege,
                associations: this.props.associations,
                selfPrivilege: this.props.selfPrivilege,
                associationIdContext: this.props.associationIdContext
              } as PersonsTabProps)}
            </Tabs.Panel>
          ))}
        </Tabs>
        <hr />
        {this.state.saving && <CenteredLoader />}
        {this.state.saveError && (
          <Alert color="red">{this.state.saveError}</Alert>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
          <Button
            color="gray"
            onClick={() => this.props.onCancel()}
          >
            {this.state.hasChanges ? "Cancel" : "Close"}
          </Button>
          <Button
            color="blue"
            disabled={!(this.state.hasChanges && invalidFields.length <= 0)}
            style={{
              width: "80px",
              display: saveButtonVisible ? "block" : "none",
            }}
            onClick={this.save}
          >
            <span id="person-save-button">
              {editMode == "new" ? "Create" : "Save"}
            </span>
          </Button>
        </div>
        {invalidFields.length > 0 && (
          <Tooltip
            label={`Invalid fields: ${invalidFields.join(", ")}`}
            position="bottom"
            opened
          >
            <span id="person-save-button-tooltip" />
          </Tooltip>
        )}

        <Modal
          opened={!!this.state.fuzzyTally}
          onClose={() => this.setState({ fuzzyTally: null })}
          size="sm"
          title="Possible Duplicate Person"
        >
          <FuzzyPersonMatch
            fuzzyTally={this.state.fuzzyTally}
            // @ts-ignore
            onAssociate={() =>
              this.associateExistingPerson(this.state.fuzzyTally.d)
            }
            onContinueToCreate={this.definitelyCreatePersonFromFuzzyMatch}
          />
        </Modal>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    // persons: state.persons.map,
    self: state.persons.map[state.persons.self],
    selfPrivilege: state.auth.privilege,
    associations: state.associations.map,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { savePerson, createPerson, getFullPersonById },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PersonsEditor);
